import { ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  LogOut, 
  User, 
  Moon,
  Sun,
  ChevronLeft,
  Building2
} from "lucide-react";
import { signOut } from "@/lib/auth";
import { toast } from "sonner";
import { useTheme } from "@/hooks/useTheme";
import { MemoAIFAB } from "@/components/MemoAI/MemoAIFAB";
import { BottomNav } from "@/components/navigation/BottomNav";
import { CommandBar, useCommandBar } from "@/components/MemoAI/CommandBar";
import logo from "@/assets/logo.png";

interface AppShellProps {
  children: ReactNode;
  title?: string;
  subtitle?: string;
  showBack?: boolean;
  backTo?: string;
  companyId?: string;
  companyName?: string;
  hideAI?: boolean;
  hideBottomNav?: boolean;
}

export function AppShell({
  children,
  title,
  subtitle,
  showBack = false,
  backTo = "/app",
  companyId,
  companyName,
  hideAI = false,
  hideBottomNav = false,
}: AppShellProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { resolvedTheme, toggleTheme } = useTheme();
  const { open: commandOpen, setOpen: setCommandOpen } = useCommandBar();

  const handleLogout = async () => {
    const { error } = await signOut();
    if (error) {
      toast.error(error.message);
    } else {
      toast.success("Logged out successfully");
      navigate("/");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b bg-background/80 backdrop-blur-xl">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex h-16 items-center justify-between gap-4">
            {/* Left side */}
            <div className="flex items-center gap-3">
              {showBack ? (
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => navigate(backTo)}
                  className="rounded-xl"
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
              ) : (
                <button 
                  onClick={() => navigate("/app")}
                  className="flex items-center gap-2.5 hover:opacity-80 transition-opacity"
                >
                  <img src={logo} alt="Memo" className="h-10 w-10 rounded-xl shadow-sm" />
                  <span className="text-xl font-semibold hidden sm:inline">Memo</span>
                </button>
              )}
              
              {title && (
                <div className="hidden sm:block pl-3 border-l">
                  <h1 className="font-semibold text-sm">{title}</h1>
                  {subtitle && (
                    <p className="text-xs text-muted-foreground">{subtitle}</p>
                  )}
                </div>
              )}
            </div>

            {/* Right side */}
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="rounded-xl"
              >
                {resolvedTheme === "dark" ? (
                  <Sun className="h-5 w-5" />
                ) : (
                  <Moon className="h-5 w-5" />
                )}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-xl">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-primary/10 text-primary text-sm">
                        <User className="h-4 w-4" />
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 rounded-xl">
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium">My Account</p>
                      <p className="text-xs text-muted-foreground">Manage your profile</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/app/profile")} className="rounded-lg">
                    <User className="mr-2 h-4 w-4" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/app")} className="rounded-lg">
                    <Building2 className="mr-2 h-4 w-4" />
                    Companies
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="rounded-lg text-danger focus:text-danger">
                    <LogOut className="mr-2 h-4 w-4" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile title bar */}
      {title && (
        <div className="sm:hidden px-4 py-3 border-b bg-muted/30">
          <h1 className="font-semibold">{title}</h1>
          {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
        </div>
      )}

      {/* Main content */}
      <main className="pb-24 sm:pb-8">
        {children}
      </main>

      {/* Memo AI FAB */}
      {!hideAI && <MemoAIFAB companyId={companyId} companyName={companyName} />}

      {/* Bottom Navigation (mobile) */}
      {!hideBottomNav && <BottomNav />}

      {/* Command Bar */}
      <CommandBar 
        open={commandOpen} 
        onOpenChange={setCommandOpen}
      />
    </div>
  );
}
